/**
 * Validation for the places this app is asked to redirect a user to after login or logout.
 *
 * Every one of those targets is attacker-influenceable - a `to` query param, a value sitting in
 * this tab's sessionStorage - so none of them reaches a navigation without passing through here.
 * The rule lives in one place because it is easy to get subtly wrong, and because the BFF holds a
 * matching copy (RedirectToUtil.isSafe) that this has to stay in agreement with.
 */

/**
 * Where a redirect lands when no safe target was supplied.
 */
export const DEFAULT_REDIRECT_TARGET = '/'

/**
 * Maximum length accepted by the BFF for a redirect target.
 */
export const MAX_REDIRECT_TARGET_LENGTH = 255

const VALIDATION_ORIGIN = 'https://redirect.invalid'
const MALFORMED_PERCENT_ESCAPE = /%(?![0-9a-f]{2})/i

/**
 * Anything outside the characters RFC 3986 allows in a relative reference: unreserved, sub-delims,
 * the gen-delims legal in a path, query, or fragment, and `%` for escapes (checked separately).
 *
 * The round-trip check below cannot stand in for this. WHATWG `URL` percent-encodes only a small
 * set inside a query, so `|`, `^`, `` ` ``, `{`, and `}` survive it unchanged and would round-trip
 * successfully. `java.net.URI`, which the BFF validates the same targets with, rejects them - so
 * without this the two would disagree.
 */
const ILLEGAL_CHARACTER = /[^A-Za-z0-9\-._~!$&'()*+,;=:@/?#%[\]]/

/**
 * True only for same-origin relative paths that are already percent-encoded ASCII.
 *
 * Backslashes are rejected outright rather than by position: browsers normalize `\` to `/` in the
 * authority slot, so `/\evil.com` leaves the origin exactly the way `//evil.com` does, and a
 * backslash anywhere else in one of these paths has no legitimate use either.
 */
export function isSafeRedirectTarget(target: string | undefined): target is string {
  if (
    target === undefined ||
    target.length > MAX_REDIRECT_TARGET_LENGTH ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    target.includes('\\') ||
    ILLEGAL_CHARACTER.test(target) ||
    MALFORMED_PERCENT_ESCAPE.test(target)
  ) {
    return false
  }

  try {
    const parsed = new URL(target, VALIDATION_ORIGIN)
    return (
      parsed.origin === VALIDATION_ORIGIN &&
      parsed.pathname + parsed.search + parsed.hash === target
    )
  } catch {
    return false
  }
}

/**
 * The target if it is safe, otherwise the default. Never returns an off-origin location.
 */
export function sanitizeRedirectTarget(target: string | undefined) {
  return isSafeRedirectTarget(target) ? target : DEFAULT_REDIRECT_TARGET
}
